import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SelectField from '../common/SelectField.js';
import Alert from '../common/Alert.js';
import { DashboardLayout } from '../common/page_component/index.js';
import Loader from "../common/Loader.js"
import salaryService from '../../services/SalaryService.js';
import { Wallet } from "lucide-react";
import { validators, inputFilters, useValidation, ValidationMessage } from '../common/Validation.js';
import Button from '../common/Button.js';
import { useTranslation } from 'react-i18next';


const SALARY_TYPE_OPTIONS = [
  { value: 'fixed_monthly', label: 'Fixed Monthly Salary' },
  { value: 'daily_wage', label: 'Daily Wages' },
];

const getBasicSalaryLabel = (salaryType) =>
  salaryType === 'daily_wage' ? 'Basic Salary (earned from attendance)' : 'Basic Salary (Monthly)';

const EditSalary = () => {
  const { t } = useTranslation();
  const { salaryId } = useParams();
  const [formData, setFormData] = useState({
    salary_type: 'fixed_monthly',
    basic_salary: '',
    allowances: '',
    deductions: '',
    bonus: '',
    overtime_pay: '',
    payment_mode: '',
    transaction_id: '',
    remarks: '',
    status: ''
  });
  const [paymentScreenshot, setPaymentScreenshot] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [existingScreenshot, setExistingScreenshot] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [netSalary, setNetSalary] = useState(0);
  const [salaryInfo, setSalaryInfo] = useState(null);
  const navigate = useNavigate();

  const { errors, validate, validateField } = useValidation();

  const validationRules = {
    basic_salary: (v) => validators.required(v, 'Basic salary') || validators.number(v, { min: 0.01, label: 'Basic salary' }),
    allowances:   (v) => v ? validators.number(v, { min: 0, label: 'Allowances' }) : '',
    deductions:   (v) => v ? validators.number(v, { min: 0, label: 'Deductions' }) : '',
    bonus:        (v) => v ? validators.number(v, { min: 0, label: 'Bonus' }) : '',
    overtime_pay: (v) => v ? validators.number(v, { min: 0, label: 'Overtime pay' }) : '',
  };

  const statuses = ['pending', 'processing', 'credited', 'hold', 'cancelled'];
  const paymentModes = ['upi', 'cash'];

  useEffect(() => {
    fetchSalary();
  }, [salaryId]);

  useEffect(() => {
    calculateNetSalary();
  }, [formData.basic_salary, formData.allowances, formData.deductions, formData.bonus, formData.overtime_pay]);

  const fetchSalary = async () => {
    try {
      const response = await salaryService.getById(salaryId);
      const salary = response.data;
      
      setSalaryInfo({
        salary_id: salary.salary_id,
        user_name: salary.user_details?.name,
        user_email: salary.user_details?.email,
        month: salary.month_display,
        year: salary.year
      });
      
      setFormData({
        salary_type: salary.salary_type || 'fixed_monthly',
        basic_salary: salary.basic_salary.toString(),
        allowances: salary.allowances.toString(),
        deductions: salary.deductions.toString(),
        bonus: salary.bonus.toString(),
        overtime_pay: salary.overtime_pay.toString(),
        payment_mode: salary.payment_mode || 'upi',
        transaction_id: salary.transaction_id || '',
        remarks: salary.remarks || '',
        status: salary.status
      });
      
      setExistingScreenshot(salary.has_screenshot);
    } catch (err) {
      Alert.error('Failed to load salary data');
      console.error(err);
      navigate('/salaries');
    } finally {
      setLoading(false);
    }
  };

  const calculateNetSalary = () => {
    const basic = parseFloat(formData.basic_salary) || 0;
    const allowances = parseFloat(formData.allowances) || 0;
    const deductions = parseFloat(formData.deductions) || 0;
    const bonus = parseFloat(formData.bonus) || 0;
    const overtime = parseFloat(formData.overtime_pay) || 0;
    
    const net = basic + allowances + bonus + overtime - deductions;
    setNetSalary(net);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    let filtered = value;
    const amountFields = ['basic_salary', 'allowances', 'deductions', 'bonus', 'overtime_pay'];
    if (amountFields.includes(name)) filtered = inputFilters.amount(value);

    setFormData((prev) => ({ ...prev, [name]: filtered }));

    if (validationRules[name]) {
      validateField(name, filtered, validationRules[name]);
    }
  };

  const handleScreenshotChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        Alert.error('Payment screenshot must be less than 5MB');
        return;
      }
      setPaymentScreenshot(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setScreenshotPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeScreenshot = () => {
    setPaymentScreenshot(null);
    setScreenshotPreview(null);
    const fileInput = document.getElementById('screenshot-input');
    if (fileInput) fileInput.value = '';
  };

  const viewExistingScreenshot = () => {
    const screenshotUrl = salaryService.getPaymentScreenshot(salaryId);
    window.open(screenshotUrl, '_blank');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate(formData, validationRules)) return;

    if (formData.status === 'credited' && !formData.payment_mode) {
      Alert.error('Please select a payment mode before marking salary as credited');
      return;
    }

    setSubmitting(true);

    try {
      await salaryService.update(salaryId, formData, paymentScreenshot);
      Alert.success('Salary record updated successfully!');
      setTimeout(() => navigate('/salaries'), 1500);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to update salary record');
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(Math.round(amount || 0));
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader fullScreen={false} size="default" message="Loading salary data..." />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-primary flex items-center gap-2">
            <Wallet className="w-6 h-6 sm:w-7 sm:h-7" />
            Edit Salary Record
          </h1>
          <p className="text-xs sm:text-sm text-gray-600 mt-1">Update salary record details</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm p-4 sm:p-6 lg:p-8 space-y-6 sm:space-y-8">
                  {/* Section 1: Record Information */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Record Information
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Salary ID</label>
                        <p className="text-sm sm:text-base text-secondary font-semibold break-all">{salaryInfo?.salary_id}</p>
                      </div>
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Employee</label>
                        <p className="text-sm sm:text-base text-secondary font-semibold break-all">{salaryInfo?.user_name}</p>
                      </div>
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Period</label>
                        <p className="text-sm sm:text-base text-secondary font-semibold break-all">
                          {salaryInfo?.month} {salaryInfo?.year}
                        </p>
                      </div>
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Status
                        </label>
                        <SelectField variant="form"
                          name="status"
                          value={formData.status}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        >
                          {statuses.map((status) => (
                            <option key={status} value={status}>
                              {status.toUpperCase()}
                            </option>
                          ))}
                        </SelectField>
                      </div>
                    </div>
                  </div>

                  {/* Section 2: Salary Components */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Salary Components
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Salary Type
                        </label>
                        <SelectField variant="form"
                          name="salary_type"
                          value={formData.salary_type}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        >
                          {SALARY_TYPE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </SelectField>
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          {getBasicSalaryLabel(formData.salary_type)} <span className="text-danger font-bold">*</span>
                        </label>
                        <input
                          type="text"
                          name="basic_salary"
                          value={formData.basic_salary}
                          onChange={handleChange}
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.basic_salary ? 'border-red-400' : 'border-gray-300'}`}
                          required
                        />
                        <ValidationMessage error={errors.basic_salary} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Allowances
                        </label>
                        <input
                          type="text"
                          name="allowances"
                          value={formData.allowances}
                          onChange={handleChange}
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.allowances ? 'border-red-400' : 'border-gray-300'}`}
                        />
                        <ValidationMessage error={errors.allowances} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Deductions
                        </label>
                        <input
                          type="text"
                          name="deductions"
                          value={formData.deductions}
                          onChange={handleChange}
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.deductions ? 'border-red-400' : 'border-gray-300'}`}
                        />
                        <ValidationMessage error={errors.deductions} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Bonus
                        </label>
                        <input
                          type="text"
                          name="bonus"
                          value={formData.bonus}
                          onChange={handleChange}
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.bonus ? 'border-red-400' : 'border-gray-300'}`}
                        />
                        <ValidationMessage error={errors.bonus} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Overtime Pay
                        </label>
                        <input
                          type="text"
                          name="overtime_pay"
                          value={formData.overtime_pay}
                          onChange={handleChange}
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.overtime_pay ? 'border-red-400' : 'border-gray-300'}`}
                        />
                        <ValidationMessage error={errors.overtime_pay} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Net Salary (Calculated)
                        </label>
                        <div className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl bg-gray-50 text-sm sm:text-base font-semibold text-green-600">
                          {formatCurrency(netSalary)}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Section 3: Payment Details */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Payment Details
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-2 gap-2 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Payment Mode
                        </label>
                        <SelectField variant="form"
                          name="payment_mode"
                          value={formData.payment_mode}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        >
                          {(!paymentModes.includes(formData.payment_mode) && formData.payment_mode
                            ? [formData.payment_mode, ...paymentModes]
                            : paymentModes
                          ).map((mode) => (
                            <option key={mode} value={mode}>
                              {mode.replace(/_/g, ' ').toUpperCase()}
                            </option>
                          ))}
                        </SelectField>
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Transaction ID
                        </label>
                        <input
                          type="text"
                          name="transaction_id"
                          value={formData.transaction_id}
                          onChange={handleChange}
                          placeholder="Enter transaction ID"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        />
                      </div>

                      <div className="sm:col-span-2">
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Payment Screenshot
                        </label>
                        
                        {existingScreenshot && !screenshotPreview && (
                          <Button
                            variant="primary"
                            onClick={viewExistingScreenshot}
                            className="mb-3"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            View Existing Screenshot
                          </Button>
                        )}
                        
                        <input
                          id="screenshot-input"
                          type="file"
                          accept="image/*,application/pdf"
                          onChange={handleScreenshotChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        />
                        <p className="text-xs text-gray-600 mt-1">
                          {existingScreenshot ? 'Upload new screenshot to replace existing one (Max 5MB)' : 'Upload payment screenshot (Max 5MB)'}
                        </p>
                        
                        {screenshotPreview && (
                          <div className="mt-3">
                            <p className="text-sm font-medium text-gray-700 mb-2">New Screenshot Preview:</p>
                            <div className="relative inline-block">
                              <img 
                                src={screenshotPreview} 
                                alt="Screenshot Preview" 
                                className="w-32 h-32 sm:w-40 sm:h-40 object-cover border border-gray-300 rounded-md"
                              />
                              <button
                                type="button"
                                onClick={removeScreenshot}
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition-colors text-sm"
                                title="Remove screenshot"
                              >
                                ×
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Section 4: Remarks */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Remarks
                    </h3>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                        Additional Notes
                      </label>
                      <textarea
                        name="remarks"
                        value={formData.remarks}
                        onChange={handleChange}
                        placeholder="Enter any additional notes or remarks"
                        rows="4"
                        className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                      />
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-0 sm:pt-0">
                    <Button
                      variant="primary"
                      type="submit"
                      loading={submitting}
                    >
                      {submitting ? 'Updating...' : 'Update Salary Record'}
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => navigate('/salaries')}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
    </DashboardLayout>
  );
};

export default EditSalary;